﻿import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/layout/AppShell';
import { PageLoader } from '@/layout/PageLoader';
import { LiveProvider } from '@/hooks/LiveProvider';
import { useAuth } from '@/hooks/useAuth';
import { AccountPage } from '@/pages/Account';
import { AdminAuditPage } from '@/pages/admin/AdminAudit';
import { AdminBillingPage } from '@/pages/admin/AdminBilling';
import { AdminInstancesPage } from '@/pages/admin/AdminInstances';
import { AdminIpamPage } from '@/pages/admin/AdminIpam';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { AdminMailPage } from '@/pages/admin/AdminMail';
import { AdminPlansPage } from '@/pages/admin/AdminPlans';
import { AdminServersPage } from '@/pages/admin/AdminServers';
import { AdminSettingsPage } from '@/pages/admin/AdminSettings';
import { AdminUsersPage } from '@/pages/admin/AdminUsers';
import { PasswordForgotPage, PasswordResetPage, VerifyEmailPage } from '@/pages/AuthFlows';
import { BillingPage } from '@/pages/Billing';
import { DashboardPage } from '@/pages/Dashboard';
import { InstanceDetailPage } from '@/pages/InstanceDetail';
import { InstancesPage } from '@/pages/Instances';
import { InvoiceDetailPage, InvoicesPage } from '@/pages/Invoices';
import { LoginPage } from '@/pages/Login';
import { PlansPage } from '@/pages/Plans';
import { paths } from '@/router/paths';

export function App() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <PageLoader />;

  if (!user) {
    return (
      <Routes>
        <Route path={paths.login} element={<LoginPage />} />
        {/* Ziele der Links aus den Mails - ohne Sitzung erreichbar */}
        <Route path={paths.passwordForgot} element={<PasswordForgotPage />} />
        <Route path={paths.passwordReset} element={<PasswordResetPage />} />
        <Route path={paths.verifyEmail} element={<VerifyEmailPage />} />
        <Route path="*" element={<Navigate to={paths.login} replace />} />
      </Routes>
    );
  }

  return (
    <LiveProvider>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="instanzen" element={<InstancesPage />} />
          <Route path="instanzen/:id" element={<InstanceDetailPage />} />
          <Route path="tarife" element={<PlansPage />} />
          <Route path="guthaben" element={<BillingPage />} />
          <Route path="rechnungen" element={<InvoicesPage />} />
          <Route path="rechnungen/:id" element={<InvoiceDetailPage />} />
          <Route path="konto" element={<AccountPage />} />

          {/* Administrationsbereich: eigene Unterrouten mit gemeinsamem Rahmen */}
          <Route
            path="admin"
            element={isAdmin ? <AdminLayout /> : <Navigate to={paths.dashboard} replace />}
          >
            <Route index element={<Navigate to={paths.admin.servers} replace />} />
            <Route path="server" element={<AdminServersPage />} />
            <Route path="ip-verwaltung" element={<AdminIpamPage />} />
            <Route path="instanzen" element={<AdminInstancesPage />} />
            <Route path="benutzer" element={<AdminUsersPage />} />
            <Route path="guthaben" element={<AdminBillingPage />} />
            <Route path="tarife" element={<AdminPlansPage />} />
            <Route path="e-mails" element={<AdminMailPage />} />
            <Route path="einstellungen" element={<AdminSettingsPage />} />
            <Route path="protokoll" element={<AdminAuditPage />} />
          </Route>
        </Route>

        {/*
          Die Ziele der Mail-Links muessen auch mit Sitzung erreichbar sein.
          Der Regelfall ist sogar genau der: Wer im Panel "Link erneut senden"
          drueckt, ist angemeldet und klickt dann in der Mail. Ohne diese
          Eintraege greift die Auffangregel unten und leitet zum Dashboard um -
          fuer den Benutzer passiert scheinbar nichts.
        */}
        <Route path={paths.verifyEmail} element={<VerifyEmailPage />} />
        <Route path={paths.passwordReset} element={<PasswordResetPage />} />
        <Route path={paths.passwordForgot} element={<PasswordForgotPage />} />

        <Route path={paths.login} element={<Navigate to={paths.dashboard} replace />} />
        <Route path="*" element={<Navigate to={paths.dashboard} replace />} />
      </Routes>
    </LiveProvider>
  );
}
